import { Activity, AppWindow, ArrowRightLeft, BarChart3, Boxes, Building2, Database, Globe, Globe2, KeyRound, LayoutDashboard, HardDrive, Link2, Mail, Package, Plug, Receipt, ScrollText, Server, Settings, ShieldAlert, ShieldCheck, UserCog, Users, Workflow, type LucideIcon } from "lucide-react";
import { Link, Outlet, useLocation } from "react-router";
import type { Permission } from "@hostdock/types";
import { AppShell, Breadcrumbs, type Crumb, type LinkLike, type NavGroup } from "@hostdock/ui";
import { useSession } from "./session";

interface NavDef {
  label: string;
  to: string;
  icon: LucideIcon;
  /** Shown if the admin holds any of these. Visibility only; the API enforces access. */
  anyOf?: Permission[];
}

/**
 * Only modules that are implemented appear here. Infrastructure, Hosting,
 * Monitoring, Backups and Integrations are added with their phases.
 */
const NAV: { label?: string; items: NavDef[] }[] = [
  { items: [{ label: "Dashboard", to: "/", icon: LayoutDashboard }] },
  {
    label: "Infrastructure",
    items: [
      { label: "Servers", to: "/infrastructure/servers", icon: Server, anyOf: ["INFRASTRUCTURE_VIEW"] },
      { label: "Regions", to: "/infrastructure/regions", icon: Globe2, anyOf: ["INFRASTRUCTURE_VIEW"] },
      { label: "Provisioning", to: "/infrastructure/provisioning", icon: Workflow, anyOf: ["INFRASTRUCTURE_VIEW"] },
    ],
  },
  {
    label: "Hosting",
    items: [
      { label: "Websites", to: "/hosting/websites", icon: Globe, anyOf: ["WEBSITE_VIEW"] },
      { label: "Applications", to: "/hosting/applications", icon: AppWindow, anyOf: ["WEBSITE_VIEW"] },
      { label: "App Installer", to: "/hosting/app-installer", icon: Boxes, anyOf: ["WEBSITE_VIEW"] },
      { label: "Databases", to: "/hosting/databases", icon: Database, anyOf: ["WEBSITE_VIEW", "DATABASE_MANAGE"] },
      { label: "Domains", to: "/hosting/domains", icon: Link2, anyOf: ["WEBSITE_VIEW"] },
      { label: "Email", to: "/hosting/email", icon: Mail, anyOf: ["MONITORING_VIEW"] },
      { label: "Backups", to: "/hosting/backups", icon: HardDrive, anyOf: ["BACKUP_VIEW"] },
      { label: "Migrations", to: "/hosting/migrations", icon: ArrowRightLeft, anyOf: ["MIGRATION_VIEW"] },
      { label: "Malware scans", to: "/hosting/security", icon: ShieldAlert, anyOf: ["WEBSITE_VIEW"] },
      { label: "Plans", to: "/hosting/plans", icon: Package, anyOf: ["BILLING_MANAGE", "CUSTOMER_VIEW", "WEBSITE_VIEW"] },
    ],
  },
  {
    label: "Customers",
    items: [
      { label: "Customers", to: "/customers", icon: Building2, anyOf: ["CUSTOMER_VIEW"] },
      { label: "Subscriptions", to: "/customers/subscriptions", icon: Receipt, anyOf: ["CUSTOMER_VIEW"] },
      { label: "Usage", to: "/customers/usage", icon: BarChart3, anyOf: ["CUSTOMER_VIEW"] },
      { label: "Billing integration", to: "/customers/billing", icon: Plug, anyOf: ["BILLING_MANAGE"] },
    ],
  },
  {
    label: "Security",
    items: [
      { label: "Admins", to: "/security/admins", icon: UserCog, anyOf: ["SECURITY_MANAGE"] },
      { label: "Roles", to: "/security/roles", icon: ShieldCheck, anyOf: ["SECURITY_MANAGE"] },
      { label: "Permissions", to: "/security/permissions", icon: KeyRound, anyOf: ["SECURITY_MANAGE"] },
      { label: "Sessions", to: "/security/sessions", icon: Users, anyOf: ["SECURITY_MANAGE"] },
      { label: "Audit Logs", to: "/security/audit", icon: ScrollText, anyOf: ["AUDIT_VIEW"] },
    ],
  },
  { items: [{ label: "Settings", to: "/settings", icon: Settings, anyOf: ["SETTINGS_MANAGE", "SECURITY_MANAGE", "MONITORING_VIEW"] }] },
];

const TITLES: [RegExp, Crumb[]][] = [
  [/^\/$/, [{ label: "Dashboard" }]],
  [/^\/infrastructure\/servers\/new$/, [{ label: "Infrastructure" }, { label: "Servers", href: "/infrastructure/servers" }, { label: "Add server" }]],
  [/^\/infrastructure\/servers\/[^/]+$/, [{ label: "Infrastructure" }, { label: "Servers", href: "/infrastructure/servers" }, { label: "Server" }]],
  [/^\/infrastructure\/servers$/, [{ label: "Infrastructure" }, { label: "Servers" }]],
  [/^\/infrastructure\/regions/, [{ label: "Infrastructure" }, { label: "Regions" }]],
  [/^\/infrastructure\/provisioning/, [{ label: "Infrastructure" }, { label: "Provisioning" }]],
  [/^\/hosting\/websites\/[^/]+$/, [{ label: "Hosting" }, { label: "Websites", href: "/hosting/websites" }, { label: "Website" }]],
  [/^\/hosting\/websites$/, [{ label: "Hosting" }, { label: "Websites" }]],
  [/^\/hosting\/applications/, [{ label: "Hosting" }, { label: "Applications" }]],
  [/^\/hosting\/databases/, [{ label: "Hosting" }, { label: "Databases" }]],
  [/^\/hosting\/domains/, [{ label: "Hosting" }, { label: "Domains" }]],
  [/^\/hosting\/email/, [{ label: "Hosting" }, { label: "Email" }]],
  [/^\/hosting\/backups/, [{ label: "Hosting" }, { label: "Backups" }]],
  [/^\/hosting\/migrations/, [{ label: "Hosting" }, { label: "Migrations" }]],
  [/^\/hosting\/security/, [{ label: "Hosting" }, { label: "Malware scans" }]],
  [/^\/hosting\/plans/, [{ label: "Hosting" }, { label: "Plans" }]],
  [/^\/customers\/subscriptions/, [{ label: "Customers" }, { label: "Subscriptions" }]],
  [/^\/customers\/usage/, [{ label: "Customers" }, { label: "Usage" }]],
  [/^\/customers\/billing/, [{ label: "Customers" }, { label: "Billing integration" }]],
  [/^\/customers\/[^/]+$/, [{ label: "Customers", href: "/customers" }, { label: "Customer" }]],
  [/^\/customers$/, [{ label: "Customers" }]],
  [/^\/security\/admins/, [{ label: "Security" }, { label: "Admins" }]],
  [/^\/security\/roles/, [{ label: "Security" }, { label: "Roles" }]],
  [/^\/security\/permissions/, [{ label: "Security" }, { label: "Permissions" }]],
  [/^\/security\/sessions/, [{ label: "Security" }, { label: "Sessions" }]],
  [/^\/security\/audit/, [{ label: "Security" }, { label: "Audit Logs" }]],
  [/^\/settings/, [{ label: "Settings" }]],
  [/^\/account/, [{ label: "My account" }]],
];

const RouterLink: LinkLike = ({ to, className, children, onClick, ...rest }) => (
  <Link to={to} className={className} onClick={onClick} {...rest}>
    {children}
  </Link>
);

export function AdminLayout() {
  const { session, can, logout } = useSession();
  const location = useLocation();
  const nav: NavGroup[] = NAV.map((g) => ({
    label: g.label,
    items: g.items.filter((i) => !i.anyOf || i.anyOf.some(can)).map(({ label, to, icon }) => ({ label, to, icon })),
  })).filter((g) => g.items.length > 0);
  const crumbs = TITLES.find(([re]) => re.test(location.pathname))?.[1] ?? [];

  return (
    <AppShell
      product="Admin"
      nav={nav}
      currentPath={location.pathname}
      Link={RouterLink}
      user={{ name: session.user.name, email: session.user.email, subtitle: session.role.name }}
      onLogout={() => void logout()}
      topbar={
        <div className="flex items-center justify-between gap-3">
          <Breadcrumbs items={crumbs} renderLink={(c) => <Link to={c.href!} className="hover:text-fg">{c.label}</Link>} />
          <Link to="/account" className="hidden items-center gap-1.5 text-[12.5px] text-muted hover:text-fg sm:flex">
            <Activity size={14} /> My account
          </Link>
        </div>
      }
    >
      <Outlet />
    </AppShell>
  );
}
